import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Loader2, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import { api, CONTACT } from "../lib/api";
import { services } from "../data/services";

const initial = { name: "", email: "", phone: "", service: "", message: "" };

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_peerwave-landing/artifacts/vkr134x1_lisa%20happy%207.jpg";

const Contact = () => {
  const [params] = useSearchParams();
  const preselect = params.get("service");
  const [form, setForm] = useState({ ...initial, service: preselect || "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (preselect) setForm((f) => ({ ...f, service: preselect }));
  }, [preselect]);

  const serviceOptions = useMemo(() => [...services.map((s) => s.title), "Other"], []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name";
    if (!form.email.trim()) e.email = "We need an email to reply";
    else if (!emailRegex.test(form.email.trim())) e.email = "That email doesn't look right";
    if (!form.message.trim()) e.message = "Add a short message so we can help";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm({ ...initial });
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const infoCards = [
    {
      key: "phone",
      icon: Phone,
      label: "Call us",
      value: CONTACT.phone,
      href: CONTACT.phoneDial,
      testId: "contact-phone-card",
    },
    {
      key: "email",
      icon: Mail,
      label: "Email us",
      value: CONTACT.email,
      href: CONTACT.emailLink,
      testId: "contact-email-card",
    },
    {
      key: "location",
      icon: MapPin,
      label: "Based on the",
      value: CONTACT.location,
      href: null,
      testId: "contact-location-card",
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero banner */}
      <section className="max-w-7xl mx-auto container-px pt-12 md:pt-16 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Contact</p>
            <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">
              Let's have <span className="wave-underline">a chat</span>.
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">
              Fill in the form, or just call us — whichever feels easier.
            </p>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="w-full overflow-hidden rounded-[2rem] ring-1 ring-black/5 shadow-lg bg-secondary">
              <img
                src={HERO_IMG}
                alt="A PeerWave participant smiling in the sunshine among tropical gardens"
                className="w-full h-auto block"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Info cards row */}
      <section className="max-w-7xl mx-auto container-px mt-10 md:mt-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {infoCards.map(({ key, icon: Icon, label, value, href, testId }) => {
            const inner = (
              <>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shrink-0">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
                  <p className="font-heading text-lg font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                    {value}
                  </p>
                </div>
              </>
            );
            return href ? (
              <a
                key={key}
                href={href}
                data-testid={testId}
                className="flex items-center gap-4 rounded-3xl bg-card border border-border/70 p-5 md:p-6 shadow-sm hover:border-primary/40 hover:-translate-y-0.5 transition-all"
              >
                {inner}
              </a>
            ) : (
              <div
                key={key}
                data-testid={testId}
                className="flex items-center gap-4 rounded-3xl bg-card border border-border/70 p-5 md:p-6 shadow-sm"
              >
                {inner}
              </div>
            );
          })}
        </div>
      </section>

      {/* Intro + Form */}
      <section className="max-w-7xl mx-auto container-px pt-14 pb-24">
        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          noValidate
          className="mt-4 rounded-[2rem] bg-card border border-border/70 p-8 md:p-12 shadow-sm"
        >
          {submitted && (
            <div
              data-testid="contact-form-success"
              className="mb-8 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm text-foreground/85"
            >
              Thanks — your message has landed with us. We'll be in touch shortly.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Your name</Label>
              <Input
                id="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Jamie Smith"
                className="h-13 rounded-2xl bg-muted border-transparent focus-visible:border-primary focus-visible:ring-0 px-4"
              />
              {errors.name && (
                <p data-testid="contact-error-name" className="text-xs text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={handleChange("email")}
                placeholder="you@example.com"
                className="h-13 rounded-2xl bg-muted border-transparent focus-visible:border-primary focus-visible:ring-0 px-4"
              />
              {errors.email && (
                <p data-testid="contact-error-email" className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">Phone (optional)</Label>
              <Input
                id="phone"
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="0400 000 000"
                className="h-13 rounded-2xl bg-muted border-transparent focus-visible:border-primary focus-visible:ring-0 px-4"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Service of interest</Label>
              <Select
                value={form.service}
                onValueChange={handleChange("service")}
              >
                <SelectTrigger
                  data-testid="contact-select-service"
                  className="h-13 rounded-2xl bg-muted border-transparent focus:ring-0 focus:border-primary px-4"
                >
                  <SelectValue placeholder="Pick a service (optional)" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {serviceOptions.map((label) => (
                    <SelectItem
                      key={label}
                      value={label}
                      data-testid={`contact-select-option-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label htmlFor="message" className="text-sm font-medium">How can we help?</Label>
            <Textarea
              id="message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={handleChange("message")}
              placeholder="Tell us a bit about you or your loved one — no wrong answers."
              rows={5}
              className="rounded-2xl bg-muted border-transparent focus-visible:border-primary focus-visible:ring-0 px-4 py-3"
            />
            {errors.message && (
              <p data-testid="contact-error-message" className="text-xs text-destructive">{errors.message}</p>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground max-w-sm">
              By submitting, you agree we may contact you about your enquiry. We never share your details.
            </p>
            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="rounded-full bg-primary text-primary-foreground h-13 px-8 hover:-translate-y-0.5 transition-transform"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send message
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Contact;
